// src/components/drillholes/data_panel/DrillholesDataPanel.tsx

import { useEffect, useState } from 'react';
import { collarsUrl, surveysUrl, drillholesUrl } from '@/api/urls';
import { CollarsWorkspace } from './CollarsWorkspace';
import { SurveysWorkspace } from './SurveysWorkspace';
import type { Collar } from './CollarsWorkspace';
import type { SurveyHole } from './SurveysWorkspace';
import { DrillholesWorkspace } from './DrillholesWorkspace';
import type { DrillholeStatus } from './DrillholesWorkspace';
import type { EntityId, CollarSubView, SurveySubView } from '../DrillholesApp';


interface Props {
  activeEntity:          EntityId;
  onEntityChange:        (id: EntityId) => void;
  collarSubView:         CollarSubView;
  onCollarSubViewChange: (v: CollarSubView) => void;
  surveySubView:         SurveySubView;
  onSurveySubViewChange: (v: SurveySubView) => void;
}

export function DrillholesDataPanel({
  activeEntity, onEntityChange,
  collarSubView, onCollarSubViewChange,
  surveySubView, onSurveySubViewChange,
}: Props) {
  const [collars,          setCollars]          = useState<Collar[]>([]);
  const [loadingCollars,   setLoadingCollars]   = useState(true);
  const [surveys,          setSurveys]          = useState<SurveyHole[]>([]);
  const [loadingSurveys,   setLoadingSurveys]   = useState(true);
  const [drillholes,       setDrillholes]       = useState<DrillholeStatus[]>([]);
  const [loadingDrillholes, setLoadingDrillholes] = useState(true);

  useEffect(() => {
    fetch(collarsUrl, { credentials: 'include' })
      .then((res) => { if (!res.ok) throw new Error(); return res.json(); })
      .then((data: Collar[]) => setCollars(data))
      .catch(() => {})
      .finally(() => setLoadingCollars(false));

    fetch(surveysUrl, { credentials: 'include' })
      .then((res) => { if (!res.ok) throw new Error(); return res.json(); })
      .then((data: SurveyHole[]) => setSurveys(data))
      .catch(() => {})
      .finally(() => setLoadingSurveys(false));

    fetch(drillholesUrl, { credentials: 'include' })
      .then((res) => { if (!res.ok) throw new Error(); return res.json(); })
      .then((data: DrillholeStatus[]) => setDrillholes(data))
      .catch(() => {})
      .finally(() => setLoadingDrillholes(false));
  }, []);

  const spatialCount   = drillholes.filter(h => h.spatial).length;
  const completeCount  = drillholes.filter(h => h.spatial && h.has_intervals).length;

  const counts: Record<EntityId, number | null> = {
    collars:    collars.length    > 0 ? collars.length  : null,
    surveys:    surveys.length    > 0 ? surveys.length  : null,
    drillholes: spatialCount      > 0 ? spatialCount    : null,
  };

  const subs: Record<EntityId, string> = {
    collars:    'holes located',
    surveys:    'directional records',
    drillholes: `spatial · ${completeCount} complete`,
  };

  const labels: Record<EntityId, string> = {
    collars:    'Collars',
    surveys:    'Surveys',
    drillholes: 'Drillholes',
  };

  const entities: EntityId[] = ['collars', 'surveys', 'drillholes'];

  return (
    <div className="flex flex-col flex-1 min-h-0">

      {/* Card strip — desktop only */}
      <div className="hidden md:grid grid-cols-3 gap-3 p-4 bg-muted/40 border-b border-border">
        {entities.map((id) => (
          <button
            key={id}
            onClick={() => onEntityChange(id)}
            className={[
              'flex flex-col items-start rounded-xl border-2 bg-background p-3 text-left transition-colors',
              activeEntity === id
                ? 'border-blue-500'
                : 'border-transparent hover:border-border/60',
            ].join(' ')}
          >
            <span className="mb-1.5 text-[11px] text-muted-foreground">{labels[id]}</span>
            {counts[id] !== null ? (
              <>
                <span className="text-xl font-medium leading-none text-foreground">
                  {counts[id]!.toLocaleString()}
                </span>
                <span className="mt-1 text-[11px] text-muted-foreground">{subs[id]}</span>
              </>
            ) : (
              <span className="mt-1 text-[13px] text-muted-foreground/60">no data yet</span>
            )}
          </button>
        ))}
      </div>

      {/* Workspace */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <div className={activeEntity === 'collars'    ? 'flex flex-1 min-h-0' : 'hidden'}>
          <CollarsWorkspace
            onCollarsLoaded={setCollars}
            collars={collars}
            loading={loadingCollars}
            subView={collarSubView}
            onSubViewChange={onCollarSubViewChange}
          />
        </div>
        <div className={activeEntity === 'surveys'    ? 'flex flex-1 min-h-0' : 'hidden'}>
          <SurveysWorkspace
            onSurveysLoaded={setSurveys}
            surveys={surveys}
            loading={loadingSurveys}
            subView={surveySubView}
            onSubViewChange={onSurveySubViewChange}
          />
        </div>
        <div className={activeEntity === 'drillholes' ? 'flex flex-1 min-h-0' : 'hidden'}>
          <DrillholesWorkspace holes={drillholes} loading={loadingDrillholes} />
        </div>
      </div>

    </div>
  );
}